import gsap from 'gsap';
import { SignalConnection } from 'typed-signals';

import { App } from '../App';
import { Settings } from '../AppConfig';
import { AnalyticsEvent } from '../plugins/AnalyticsPlugin';
import { RewardItem } from '../plugins/RewardPlugin';
import { StrUtils } from '../utils/StrUtils';
import { RewardAdButton } from './buttons/RewardAdButton';
import { RewardButton } from './buttons/RewardButton';
import { Panel } from './Panel';
import { Widget } from './Widget';

export class RewardsPanel extends Widget
{
    public app: App;
    private panel: Panel;
    private dailyRewardButton: RewardAdButton;
    private playtimeRewardButton: RewardAdButton;
    private updateRewardConnection?: SignalConnection;
    
    constructor(app: App)
    {
        super();
        this.app = app;
        
        this.panel = new Panel({
            w: this.width,
            h: this.height,
        });
        this.view.addChild(this.panel.view);

        this.dailyRewardButton = new RewardAdButton({
            buttonText: 'homeScreen_dailyRewardText',
            titleText: 'homeScreen_dailyRewardTitle',
            nextText: 'homeScreen_dailyRewardNext',
            buttonAsset: 'button-ninesliced-magenta',
        });

        this.dailyRewardButton.view.y -= 93;
        this.panel.view.addChild(this.dailyRewardButton.view);
        this.dailyRewardButton.onPress.connect(() =>
        {
            const reward = this.app.reward.getRandomReward(this.app.reward.getDailyRewards());
            this.app.analytics.trackEvent(AnalyticsEvent.HOMEPAGE_REWARDCLAIM_CLICK, { interaction: "reward claim|daily reward"});
            this.app.reward.claimDailyReward(reward, this.dailyRewardButton.adIcon.visible);
        });

        this.playtimeRewardButton = new RewardAdButton({
            buttonText: 'homeScreen_playtimeRewardText',
            titleText: 'homeScreen_playtimeRewardTitle',
            nextText: 'homeScreen_playtimeRewardNext',
        });

        this.playtimeRewardButton.view.y += 85;
        this.playtimeRewardButton.onPress.connect(() =>
        {
            const reward = this.app.reward.getRandomReward(this.app.reward.getPlaytimeRewards());
            this.app.analytics.trackEvent(AnalyticsEvent.HOMEPAGE_REWARDCLAIM_CLICK, { interaction: "reward claim|playtime reward"});
            this.app.reward.claimPlaytimeReward(reward, this.playtimeRewardButton.adIcon.visible);
        });
        this.panel.view.addChild(this.playtimeRewardButton.view);
    }

    public get width(): number
    {
        return 290;
    }

    public get height(): number
    {
        return 400;
    }

    protected async onOpen(): Promise<void>
    {
        this.updateRewardConnection = this.app.reward.onRewardUpdate.connect(() => this.refresh());
        this.refresh();
        gsap.killTweensOf(this.panel.view);
        this.panel.view.x = 500;
        await gsap.to(this.panel.view, { x: 0, duration: 0.5, ease: 'expo.out', delay: 0.1 });
    }

    protected async onClose(): Promise<void>
    {
        if (this.updateRewardConnection) this.updateRewardConnection.disconnect();
        this.updateRewardConnection = undefined;
        gsap.killTweensOf(this.panel.view);
        await gsap.to(this.panel.view, { x: 500, duration: 0.3, ease: 'sine.in' });
    }

    private refresh(): void
    {
        this.dailyRewardButton.setAdIconVisible(this.app.ads.config.dailyRewardAds && this.app.ads.canShowAd());
        this.playtimeRewardButton.setAdIconVisible(this.app.ads.config.playtimeRewardAds && this.app.ads.canShowAd());
        
        if (this.app.reward.canClaimDailyReward())
        {
            this.dailyRewardButton.modeClaim();
        }
        else
        {
            this.dailyRewardButton.modeWaiting();
        }

        if (this.app.reward.canClaimPlaytimeReward())
        {
            this.playtimeRewardButton.buttonText.setText('homeScreen_playtimeRewardText');
            this.playtimeRewardButton.modeClaim();
        }
        else if (this.app.reward.isCountingDownPlaytimeReward())
        {
            this.playtimeRewardButton.buttonText.text = StrUtils.formatTime(this.app.reward.getPlaytimeRweardCountdown());
            this.playtimeRewardButton.modeCountdown();
        }
        else
        {
            this.playtimeRewardButton.modeWaiting();
        }
    }
}
